<template>
  <UIModal
    class="ModalPersonalisation"
    size="lg"
    :isOpen="isOpen"
    @close="emit('close')"
  >
    <template #title>
      <div class="ModalPersonalisation__title">Personalise EverGPT</div>
    </template>

    <template #content>
      <form id="modal-personalisation-form" @submit.prevent="onSubmit">
        <p class="ModalPersonalisation__description">
          These preferences help EverGPT provide responses that better match
          your role, tone, and working style.
        </p>

        <div class="ModalPersonalisation__form">
          <UIInputText
            name="name"
            isSanitised
            label="Preferred name"
            placeholder="What should we call you?"
          />
          <UIInputText
            name="role"
            isSanitised
            label="Your role"
            placeholder="e.g., Product Manager, Software Engineer, Designer"
          />

          <UIInputTextArea
            name="job_context"
            isSanitised
            label="What you're working on"
            placeholder="e.g., Leading [X] project, researching [Y], managing [Z]"
            description="Share what you're currently working on so EverGPT can give you more relevant, contextual responses."
            :rows="5"
          />

          <UIInputRadioGroup
            class="ModalPersonalisation__style-tone"
            name="tone_of_voice"
            label="Base style & tone"
            description="Choose how you want EverGPT to communicate with you"
            direction="horizontal"
            :items="TONE_ITEMS"
          />

          <div class="ModalPersonalisation__chat-layout">
            <UIInputLabel>Chat layout</UIInputLabel>

            <div class="ModalPersonalisation__chat-layout-options">
              <UIInputRadio
                id="compact"
                name="ui_layout"
                label="Compact"
                value="compact"
              >
                <template #label>
                  <UIIcon
                    class="ModalPersonalisation__chat-layout-options-icon"
                    name="displayCompact"
                  />
                  <div
                    class="ModalPersonalisation__chat-layout-options-content"
                  >
                    <div
                      class="ModalPersonalisation__chat-layout-options-content-title"
                    >
                      Compact (Default)
                    </div>
                    <p
                      class="ModalPersonalisation__chat-layout-options-content-description"
                    >
                      Fits more on your screen at once
                    </p>
                  </div>
                </template>
              </UIInputRadio>

              <UIInputRadio
                id="comfortable"
                name="ui_layout"
                label="Comfortable"
                value="comfortable"
              >
                <template #label>
                  <UIIcon
                    class="ModalPersonalisation__chat-layout-options-icon"
                    name="displayCompact"
                  />
                  <div
                    class="ModalPersonalisation__chat-layout-options-content"
                  >
                    <div
                      class="ModalPersonalisation__chat-layout-options-content-title"
                    >
                      Comfortable
                    </div>
                    <p
                      class="ModalPersonalisation__chat-layout-options-content-description"
                    >
                      More spacing, easier to scan
                    </p>
                  </div>
                </template>
              </UIInputRadio>
            </div>
          </div>
        </div>
      </form>
    </template>

    <template #actions>
      <UIButton theme="secondary" @click.prevent="$emit('close')">
        Cancel
      </UIButton>
      <UIButton
        form="modal-personalisation-form"
        theme="primary"
        type="submit"
        :isLoading="isLoading"
      >
        Save Preferences
      </UIButton>
    </template>
  </UIModal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

import {
  UIButton,
  UIInputLabel,
  UIInputRadio,
  UIInputRadioGroup,
  UIInputText,
  UIInputTextArea,
  UIModal,
  type UIInputRadioOption,
} from '@foundever/ui-lib'
import { useForm } from 'vee-validate'
import { string } from 'yup'

import { handleApiErrorToast } from '@/apiClient'
import { AuthApi } from '@/apiClient/modules/auth'
import type { IAuthUser } from '@/apiClient/types/authUser'
import { useMetricEvents } from '@/composables/useMetricEvents'
import { useAuthStore } from '@/store/auth'
import { useToast } from '@/store/toast'

import UIIcon from '../ui/icons/UIIcon.vue'

const TONE_ITEMS: UIInputRadioOption[] = [
  { id: 'default', label: 'Standard', value: 'default' },
  { id: 'professional', label: 'Professional', value: 'professional' },
  { id: 'friendly', label: 'Friendly', value: 'friendly' },
  { id: 'concise', label: 'Concise', value: 'concise' },
]

const props = defineProps<{
  isOpen: boolean
}>()

const authStore = useAuthStore()
const toast = useToast()

const { handleSubmit, resetForm } = useForm<
  IAuthUser['settings']['personalized_settings']
>({
  validationSchema: {
    name: string(),
    role: string(),
    job_context: string(),
    tone_of_voice: string().oneOf([
      'default',
      'professional',
      'friendly',
      'concise',
    ]),
    ui_layout: string().oneOf(['compact', 'comfortable']),
  },
  initialValues: {
    name: '',
    role: '',
    job_context: '',
    tone_of_voice: 'default',
    ui_layout: 'compact',
  },
})

// Watch for user data changes and reset form with personalized settings
watch(
  () => props.isOpen,
  (isOpen) => {
    if (!isOpen) return

    const personalizedSettings = authStore.user?.settings?.personalized_settings
    if (!personalizedSettings) return

    resetForm({
      values: {
        name: personalizedSettings.name || '',
        role: personalizedSettings.role || '',
        job_context: personalizedSettings.job_context || '',
        tone_of_voice: personalizedSettings.tone_of_voice || 'default',
        ui_layout: personalizedSettings.ui_layout || 'compact',
      },
    })
  },
  { immediate: true },
)

const isLoading = ref(false)
const emit = defineEmits<{
  close: []
}>()

const onSubmit = handleSubmit(async (values) => {
  if (isLoading.value || !authStore.user) return

  isLoading.value = true

  // Merge updated personalized_settings with existing settings
  const updatedSettings: IAuthUser['settings'] = {
    ...authStore.user.settings,
    flags: {
      has_used_personalisation_settings: true, // This will disable the personalisation notification
    },
    personalized_settings: {
      ...authStore.user.settings.personalized_settings,
      ...values,
    },
  }

  useMetricEvents().sendEvent('personalisation_settings_updated')
  AuthApi.updateMe({
    email: authStore.user.email,
    settings: updatedSettings,
  })
    .then((user) => {
      authStore.setUser(user)
      toast.onSuccess('Preferences saved successfully')
    })
    .catch(handleApiErrorToast)
    .finally(() => {
      isLoading.value = false
      emit('close')
    })
})
</script>

<style lang="scss" scoped>
.ModalPersonalisation {
  .ModalPersonalisation__title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .ModalPersonalisation__description {
    margin-bottom: 1.25rem;
  }

  .ModalPersonalisation__form {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .ModalPersonalisation__chat-layout-options {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    :deep(.UIInputRadioBase__side-label) {
      display: flex;
      align-items: center;
    }
  }

  .ModalPersonalisation__chat-layout-options-icon {
    --size: 2.5rem;
    margin-left: 0.25rem;
    margin-right: 0.5rem;
  }

  .ModalPersonalisation__chat-layout-options-content {
    display: flex;
    flex-direction: column;
  }

  .ModalPersonalisation__chat-layout-options-content-title {
    @include font-size-14px;
    color: var(--text-primary);
    font-weight: 500;
  }

  .ModalPersonalisation__chat-layout-options-content-description {
    @include font-size-12px;
    color: var(--text-tertiary);
    font-weight: 400;
  }
}
</style>
